import { useEffect, useRef, useState } from 'react';
import type { Device, DeviceWithOrg, PairingCodeIssued } from '@cornerstone/shared';
import { API_BASE, PLAYER_URL } from '../client/config';
import { api } from '../client/api';
import type { WorkflowScope } from '../pages/mock/ContentApproval';
import { ModalShell } from './menu';
import { Button } from './ui';
import { describeApiError, toast } from './toast';

// =============================================================================
// DevicePairing — 기기 추가(페어링) 흐름 전부. 2026-09-14 구성 관리 통합 때 Devices.tsx에서 분리.
//   • 권한 판정: canPair / pairingGroups (Owner·Admin이 담당 그룹에만)
//   • 대상 스코프 조회: readPairingScope / usePairingTarget
//   • 코드 발급·완료 감지: issueNewDeviceCode / makeNewDeviceDetector
//   • 모달: NewDevicePairing(그룹 선택 → PairingModal), PairingModal(코드·카운트다운·감지; 갱신에도 재사용)
// 구성 관리 > 그룹·디바이스 화면은 고른 그룹을 initialGroupId로 넘겨 그룹 선택 단계를 건너뛴다.
// =============================================================================

// BO 응답은 orgName을 포함(DeviceWithOrg), FO 응답은 Device — 런타임 겸용.
export type DeviceRow = Device & Partial<Pick<DeviceWithOrg, 'orgId' | 'orgName'>>;

export function pairingErrorStatus(reason: unknown): string | undefined {
  const message = reason instanceof Error ? reason.message : String(reason);
  return /(?:HTTP\s+)?\b(401|403|404|409|423|400|5\d\d)\b/.exec(message)?.[1] ?? (/device management permission|forbidden/i.test(message) ? '403' : undefined);
}
export function pairingError(reason: unknown): string {
  const message = reason instanceof Error ? reason.message : String(reason);
  const status = pairingErrorStatus(reason);
  if (status === '403') return '등록/연결 권한 없음: 선택한 조직·그룹에서 이 작업을 수행할 수 없습니다. 조직 최고 관리자에게 권한을 확인해 주세요.';
  if (status === '401') return '로그인이 만료되었습니다. 다시 로그인한 후 기기 등록/연결을 진행해 주세요.';
  if (status === '400') return '대상 그룹이 지정되지 않았거나 변경되었습니다. 조직과 그룹을 다시 선택해 주세요.';
  if (status) return describeApiError(new Error(status));
  if (/^[가-힣]/.test(message) && !/[{}]/.test(message)) return message;
  return '일시적으로 기기 연결 정보를 확인하지 못했습니다. 네트워크 연결을 확인하고 다시 시도해 주세요.';
}
export const pairingGroups = (scope: WorkflowScope) => scope.groups.filter(group => ['Owner', 'Admin'].includes(scope.actor.role ?? '') &&
  (!scope.actor.enforced || scope.actor.central || scope.actor.groups.some(grant => grant.groupId === group.id && ['Owner', 'Admin'].includes(grant.role))));
export const canPair = (scope: WorkflowScope) => ['Owner', 'Admin'].includes(scope.actor.role ?? '') && (!scope.actor.enforced || pairingGroups(scope).length > 0);

export async function readPairingScope(orgId: string, signal?: AbortSignal): Promise<WorkflowScope> {
  const response = await fetch(`${API_BASE}/api/device-groups?orgId=${encodeURIComponent(orgId)}`, { credentials: 'include', cache: 'no-store', ...(signal ? { signal } : {}) });
  if (!response.ok) throw new Error(String(response.status));
  const scope = await response.json() as WorkflowScope;
  if (scope.actor.orgId !== orgId) throw new Error('403 target organization changed');
  return scope;
}

export async function pairingDevices(scope: WorkflowScope, signal?: AbortSignal): Promise<Array<DeviceRow & { groupId?: string | null }>> {
  if (scope.actor.enforced || scope.actor.central) return scope.devices;
  // Legacy Admin's group endpoint can omit ungrouped devices. The regular
  // list is usable only after confirming it belongs to this session's own org.
  const response = await fetch(`${API_BASE}/api/device-groups`, { credentials: 'include', cache: 'no-store', ...(signal ? { signal } : {}) });
  if (!response.ok) throw new Error(String(response.status));
  const current = await response.json() as WorkflowScope;
  if (current.actor.orgId !== scope.actor.orgId || current.actor.enforced || !canPair(current)) throw new Error('403 pairing organization changed');
  const devices = await api.listDevices() as DeviceRow[];
  return devices.filter(device => device.orgId === undefined || device.orgId === scope.actor.orgId);
}

export function usePairingTarget(orgId: string, ready: boolean, context: string) {
  const [revision, setRevision] = useState(0);
  const key = `${orgId}:${context}:${revision}`;
  const [result, setResult] = useState<{ key: string; scope?: WorkflowScope; error?: string } | null>(null);
  useEffect(() => {
    const controller = new AbortController(); setResult(null);
    if (ready && orgId) void readPairingScope(orgId, controller.signal)
      .then(scope => { if (!controller.signal.aborted) setResult({ key, scope }); })
      .catch(reason => { if (!controller.signal.aborted) setResult({ key, error: pairingError(reason) }); });
    return () => controller.abort();
  }, [orgId, ready, key]);
  const current = ready && result?.key === key ? result : null;
  return { key, scope: current?.scope ?? null, error: current?.error ?? null, loading: ready && !!orgId && !current,
    reload: () => setRevision(value => value + 1) };
}

// -----------------------------------------------------------------------------
// 디바이스 페어링 팝업 — 인증코드 발급/표시, 복사, 유효 시간 카운트다운+프로그레스바.
// 만료 시 '인증코드가 만료되었습니다.' 토스트와 함께 닫힘. 페어링 완료를 3초
// 폴링(detectPaired)으로 감지. 발급/감지 로직을 주입받아 신규 추가와
// 디바이스 갱신(BO) 양쪽에서 재사용한다.
// -----------------------------------------------------------------------------

/** 신규 디바이스 추가용 코드 발급 — 이름은 페어링 후 수정 화면에서 지정. */
export async function issueNewDeviceCode(orgId?: string | null, groupId?: string | null, signal?: AbortSignal): Promise<PairingCodeIssued> {
  const r = await fetch(`${API_BASE}/api/devices/pair-codes`, {
    method: 'POST',
    credentials: 'include',
    ...(signal ? { signal } : {}),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deviceName: '새 디바이스',
      siteLabel: null,
      deviceType: 'generic',
      groupLabel: null,
      // BRICK-147 — 최고 관리자만 대상 조직을 지정할 수 있다. 서버가 BO 권한을
      // 재검증하고, 아니면 호출자 조직으로 강제한다.
      ...(orgId ? { orgId } : {}),
      ...(groupId ? { groupId } : {}),
    }),
  });
  if (!r.ok) throw new Error(String(r.status));
  return (await r.json()) as PairingCodeIssued;
}

/** Scope-bound fallback until the server exposes code ID -> registered device status. */
export function makeNewDeviceDetector(knownIds: Set<string>, target: { orgId: string; groupId: string | null }) {
  return async (_code?: PairingCodeIssued, signal?: AbortSignal): Promise<string | null> => {
    const scope = await readPairingScope(target.orgId, signal);
    if (!canPair(scope) || target.groupId && !pairingGroups(scope).some(group => group.id === target.groupId)) throw new Error('403 pairing scope changed');
    const candidates = (await pairingDevices(scope, signal)).filter(device => !device.revokedAt && !knownIds.has(device.id) && device.pairedAt &&
      (!target.groupId || device.groupId === target.groupId));
    return candidates.length === 1 ? candidates[0]!.id : null;
  };
}

export function NewDevicePairing({ scope, initialGroupId, onClose, onPaired }: { scope: WorkflowScope; /** 구성 관리에서 고른 그룹 — 페어링 가능 그룹이면 선택 단계를 건너뛴다. */ initialGroupId?: string | null; onClose: () => void; onPaired: (id: string) => void }) {
  const preset = initialGroupId && pairingGroups(scope).some(group => group.id === initialGroupId) ? initialGroupId : '';
  const assigned = preset || (!scope.actor.central && pairingGroups(scope).length === 1 ? pairingGroups(scope)[0]!.id : '');
  const [groupId, setGroupId] = useState(assigned);
  const [confirmed, setConfirmed] = useState(!scope.actor.enforced || !!assigned);
  const baseline = useRef(new Set<string>());
  const groups = pairingGroups(scope);
  // 제출·취소는 ModalShell의 고정 푸터로(검수 S3) — 본문이 길어져도 화면 밖으로 밀리지 않는다.
  if (!confirmed) return <ModalShell title="디바이스 페어링" onClose={onClose}
    footer={<>
      <Button onClick={onClose}>취소</Button>
      <Button variant="primary" disabled={!groups.some(group => group.id === groupId)} onClick={() => { if (groups.some(group => group.id === groupId)) setConfirmed(true); }}>인증코드 발급</Button>
    </>}>
    <p className="text-[14px]">연결할 조직: {scope.orgName ?? scope.actor.orgId}</p>
    <p className="text-[13px] text-text-dim">이 조직은 그룹을 선택해야 인증코드를 발급할 수 있습니다.</p>
    <label className="text-[14px]">페어링 대상 그룹<select aria-label="페어링 대상 그룹" value={groupId} onChange={event => setGroupId(event.target.value)}
      className="mt-1 w-full rounded border border-line bg-panel-2 p-2 text-text">
      <option value="">그룹을 선택하세요</option>{groups.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
    </select></label>
  </ModalShell>;
  return <PairingModal
    issueCode={async signal => {
      // Recheck the selected organization on every issuance/retry, including BO "내 조직".
      const current = await readPairingScope(scope.actor.orgId, signal);
      if (signal?.aborted) throw new Error('요청이 취소되었습니다.');
      if (!canPair(current)) throw new Error('403 device management permission required');
      if (current.actor.enforced && (!groupId || !pairingGroups(current).some(group => group.id === groupId))) throw new Error('기기를 추가할 그룹을 다시 선택해 주세요.');
      baseline.current = new Set((await pairingDevices(current, signal)).map(device => device.id));
      if (signal?.aborted) throw new Error('요청이 취소되었습니다.');
      return issueNewDeviceCode(current.actor.orgId, current.actor.enforced ? groupId : null, signal);
    }}
    detectPaired={(code, signal) => makeNewDeviceDetector(baseline.current, { orgId: scope.actor.orgId, groupId: scope.actor.enforced ? groupId : null })(code, signal)}
    onClose={onClose} onPaired={onPaired}
  />;
}

export function PairingModal({
  issueCode,
  detectPaired,
  onClose,
  onPaired,
}: {
  issueCode: (signal?: AbortSignal) => Promise<PairingCodeIssued>;
  detectPaired: (code?: PairingCodeIssued, signal?: AbortSignal) => Promise<string | null>;
  onClose: () => void;
  onPaired: (deviceId: string) => void;
}) {
  const [issued, setIssued] = useState<PairingCodeIssued | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<string | undefined>();
  const [remainSec, setRemainSec] = useState<number | null>(null);
  const totalSecRef = useRef<number>(600);
  const expiredRef = useRef(false);
  const [retry, setRetry] = useState(0);
  const valid = useRef(true);
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const close = () => { valid.current = false; setIssued(null); onClose(); };

  useEffect(() => {
    const invalidate = () => { valid.current = false; setIssued(null); onCloseRef.current(); };
    window.addEventListener('bricklit:scope-changed', invalidate);
    window.addEventListener('bricklit:auth-changed', invalidate);
    window.addEventListener('bricklit:logout', invalidate);
    return () => {
      valid.current = false;
      window.removeEventListener('bricklit:scope-changed', invalidate);
      window.removeEventListener('bricklit:auth-changed', invalidate);
      window.removeEventListener('bricklit:logout', invalidate);
    };
  }, []);

  // 코드 발급 — 발급 방식은 호출자 주입(신규 pair-codes / 갱신 renew-code).
  useEffect(() => {
    const controller = new AbortController();
    valid.current = true; setIssued(null); setError(null); setErrorStatus(undefined); setRemainSec(null); expiredRef.current = false;
    void issueCode(controller.signal)
      .then((code) => {
        if (controller.signal.aborted || !valid.current) return;
        const total = Math.max(1, Math.round((new Date(code.expiresAt).getTime() - Date.now()) / 1000));
        totalSecRef.current = total;
        setRemainSec(total);
        setIssued(code);
      })
      .catch((e) => {
        if (!controller.signal.aborted && valid.current) {
          const message = pairingError(e);
          setError(message); setErrorStatus(pairingErrorStatus(e));
          if (pairingErrorStatus(e) !== '403') toast(`디바이스 페어링에 실패했습니다. ${message}`, 'error');
        }
      });
    return () => {
      controller.abort();
    };
  }, [retry]);

  // 카운트다운 — 만료 시 토스트 + 닫기.
  useEffect(() => {
    if (!issued) return;
    const t = setInterval(() => {
      const remain = Math.max(0, Math.round((new Date(issued.expiresAt).getTime() - Date.now()) / 1000));
      setRemainSec(remain);
      if (remain <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        valid.current = false;
        toast('인증코드가 만료되었습니다.', 'error');
        onClose();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [issued, onClose]);

  // 페어링 완료 감지 — 3초 폴링, 감지 방식은 호출자 주입.
  useEffect(() => {
    if (!issued) return;
    const controller = new AbortController(); let reading = false; let completed = false;
    const t = setInterval(() => {
      if (reading || completed || !valid.current) return;
      reading = true;
      void detectPaired(issued, controller.signal)
        .then((id) => {
          if (id && !controller.signal.aborted && valid.current && Date.parse(issued.expiresAt) > Date.now()) { completed = true; onPaired(id); }
        })
        .catch(reason => {
          if (!controller.signal.aborted && valid.current && ['401', '403'].includes(pairingErrorStatus(reason) ?? '')) {
            setIssued(null); setError(pairingError(reason)); setErrorStatus(pairingErrorStatus(reason));
          }
        }).finally(() => { reading = false; }); // Other transient errors retry on the next poll.
    }, 3000);
    return () => { controller.abort(); clearInterval(t); };
  }, [issued, detectPaired, onPaired]);

  const copy = async () => {
    if (!issued || !valid.current) return;
    try {
      await navigator.clipboard.writeText(issued.code);
      toast('클립보드에 복사되었습니다.', 'success');
    } catch {
      toast('복사에 실패했습니다.', 'error');
    }
  };

  const pct = remainSec !== null ? Math.max(0, Math.min(100, (remainSec / totalSecRef.current) * 100)) : 0;

  return (
    <ModalShell title="디바이스 페어링" onClose={close}>
      <div className="flex flex-col gap-4">
        {error && <div className="space-y-2"><p role="alert" className="text-[13px] text-red">{error}</p><Button onClick={() => setRetry(value => value + 1)}>{errorStatus === '403' ? '권한 다시 확인' : '다시 시도'}</Button></div>}
        {!issued && !error && (
          <p role="status" className="text-[14px] text-text-dim py-6 text-center">인증코드 발급 중…</p>
        )}
        {issued && (
          <>
            {/* 인증코드 + 복사 */}
            <div className="flex items-center justify-center gap-2">
              <span className="font-mono text-[22px] font-bold tracking-[0.04em] whitespace-nowrap select-all">
                {issued.code}
              </span>
              <button
                type="button"
                title="복사"
                onClick={() => void copy()}
                className="w-8 h-8 flex items-center justify-center rounded border border-line text-text-dim hover:text-text hover:border-line-strong"
              >
                ⧉
              </button>
            </div>

            {/* 유효 시간 카운트다운 + 프로그레스바 */}
            <div className="flex flex-col gap-1.5">
              <div className="text-center text-[13px] text-text-dim">
                인증코드 유효 시간{' '}
                <span className="font-mono font-semibold text-accent">{remainSec ?? 0}</span> 초
              </div>
              <div className="h-1.5 rounded-full bg-panel-3 overflow-hidden">
                <div
                  className="h-full bg-accent transition-[width] duration-1000 ease-linear"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            {/* 등록 기기에서 열 Player 주소 — 인증코드 입력 위치 안내(Figma). */}
            <div className="rounded-md border border-line bg-panel-2 p-3 flex flex-col gap-2">
              <p className="text-[12px] text-text-dim leading-snug">
                등록할 기기의 브라우저에서 아래 주소를 열어, 발급된 인증코드를 입력해
                등록을 완료하세요.
              </p>
              <div className="flex items-center justify-between gap-2 rounded border border-line bg-panel px-2.5 py-1.5">
                <span className="font-mono text-[13px] text-text select-all truncate">{PLAYER_URL}</span>
                <button
                  type="button"
                  title="주소 복사"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(PLAYER_URL)
                      .then(() => toast('주소가 복사되었습니다.', 'success'))
                      .catch(() => toast('복사에 실패했습니다.', 'error'));
                  }}
                  className="shrink-0 w-7 h-7 flex items-center justify-center rounded border border-line text-text-dim hover:text-text hover:border-line-strong"
                >
                  ⧉
                </button>
              </div>
            </div>

            <p className="text-[12px] text-text-dim leading-snug">
              ⓘ 유효 시간 내 인증코드를 입력해야 하며, 초과 시 새로 발급한 인증코드를
              사용해야 합니다. 인증코드 인증이 완료되면 자동으로 완료됩니다.
            </p>
          </>
        )}
      </div>
    </ModalShell>
  );
}
